import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { TaskConfirmationModal, type TaskFormData } from "@/components/task-confirmation-modal";
import { openUpgradeModalFromError } from "@/components/upgrade-modal";
import { AUTHENTICATED_HEADINGS } from "@/constants/heading-variants";
import { usePlaceholderRotation } from "@/hooks/use-placeholder-rotation";
import { useTextareaAutoGrow } from "@/hooks/use-textarea-auto-grow";
import { getQueryClient } from "@/integrations/tanstack-query/root-provider";
import { authClient } from "@/lib/api/auth-client";
import type { components } from "@/lib/api/types";
import { clearPendingTask, getPendingTask, savePendingTask } from "@/lib/pending-task-storage";
import { useBubbleStream } from "@/lib/streaming/use-bubble-stream";
import { useCombinedTools } from "@/lib/tasks/hooks/use-combined-tools";
import { useIntegrationIdMapping } from "@/lib/tasks/hooks/use-integration-id-mapping";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import {
	ScheduledTaskSuccessDialog,
	TaskCreationHero,
	UnauthenticatedHowItWorks,
	WebhookUrlDialog,
} from "./creation-shell";
import { executeTaskNow } from "./execute-task-now";
import { generateCronExpression } from "./schedule-utils";
import { TaskInputForm } from "./task-input-form";
import { TemplateGallery } from "./template-gallery";
import { useTaskToolSelection } from "./use-task-tool-selection";

/**
 * Main task creation form component.
 *
 * Handles task creation, integration selection, and navigates to /runs/$runId for execution.
 */
interface TaskCreationFormProps {
	initialMaxWidth?: "3xl" | "4xl" | "5xl" | "6xl" | "7xl";
}

const maxWidthClasses = {
	"3xl": "max-w-3xl",
	"4xl": "max-w-4xl",
	"5xl": "max-w-5xl",
	"6xl": "max-w-6xl",
	"7xl": "max-w-7xl",
} as const;

export function TaskCreationForm({ initialMaxWidth = "4xl" }: TaskCreationFormProps) {
	// Form state
	const [instructions, setInstructions] = useState("");
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [permissionMode, setPermissionMode] = useState<"yolo" | "safe" | "plan">("safe");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isQuickCreateStarting, setIsQuickCreateStarting] = useState(false);
	const [successData, setSuccessData] = useState<QuickCreateResponse | null>(null);
	const [webhookDialogUrl, setWebhookDialogUrl] = useState<string | null>(null);
	const [heading] = useState(
		() => AUTHENTICATED_HEADINGS[Math.floor(Math.random() * AUTHENTICATED_HEADINGS.length)]
	);

	// Refs
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const [focused, setFocused] = useState(false);

	// Auth & navigation
	const accessToken = useAuthStore((state) => state.accessToken);
	const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
	const navigate = useNavigate();
	const { runId, start, stop, isStreaming, bubbles } = useBubbleStream();

	// Integration mapping
	const { userIntegrations, appIntegrations } = useIntegrationIdMapping({
		enabled: isAuthenticated,
	});

	const combinedTools = useCombinedTools(userIntegrations, appIntegrations);
	const allIntegrationIds = useMemo(() => {
		return userIntegrations
			.map((i) => Number(i.app_integration_id))
			.filter((id): id is number => Number.isFinite(id));
	}, [userIntegrations]);
	const {
		selectedTools,
		selectedIntegrationIds,
		setSelectedTools,
		setSelectedIntegrationIds,
		handleToggleTool,
	} = useTaskToolSelection(allIntegrationIds);

	const { placeholder, placeholderVisible } = usePlaceholderRotation(
		focused,
		instructions.length > 0
	);

	useTextareaAutoGrow(textareaRef, 116, 400);

	interface ScheduledTaskPayload {
		requestBody: QuickCreateRequest;
	}

	// Task creation mutation (non-streaming schedules)
	const createTaskMutation = useMutation({
		mutationFn: async ({ requestBody }: ScheduledTaskPayload) => {
			const response = await authClient.POST("/api/v1/tasks/quick-create", {
				body: requestBody,
			});

			if (response.error) {
				const errorMessage = Array.isArray(response.error.detail)
					? response.error.detail.map((e) => e.msg).join(", ")
					: (response.error.detail as string | undefined) || "Failed to create task";
				const error = new Error(errorMessage);
				// Attach status code and response data for downstream error handling (e.g., 402 payment required)
				(error as Error & { statusCode?: number }).statusCode = response.response?.status;
				(error as Error & { data?: Record<string, unknown> }).data = response.error as Record<
					string,
					unknown
				>;
				throw error;
			}

			return response.data;
		},
		onSuccess: (data, variables) => {
			const schedule = variables.requestBody.schedule;
			if (schedule !== "now") {
				getQueryClient()?.invalidateQueries({ queryKey: ["agents"] });
				setIsModalOpen(false);
				if (data?.webhook_url) {
					setWebhookDialogUrl(data.webhook_url);
				} else {
					setSuccessData(data ?? null);
				}
				setInstructions("");
			}
		},
		onError: (error: Error) => {
			const statusCode = (error as Error & { statusCode?: number }).statusCode;
			const errorData = (error as Error & { data?: Record<string, unknown> }).data;

			if (statusCode === 402) {
				setIsModalOpen(false);
				openUpgradeModalFromError(errorData);
				return;
			}

			if (statusCode === 403 && errorData?.error_code === "EMAIL_VERIFICATION_REQUIRED") {
				setIsModalOpen(false);
				savePendingTask({
					instructions,
					schedule: "now",
					permissionMode,
					selectedTools,
					selectedIntegrationIds,
					inboundEmailEnabled: undefined,
				});
				return;
			}

			// Generic error for other failures
			toast.error("Failed to create task", {
				description: error.message,
			});
		},
	});

	// Event handlers
	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (!instructions.trim()) return;
		setIsModalOpen(true);
	};

	const handleModalSubmit = (data: TaskFormData) => {
		try {
			// Check authentication first for all schedule types
			if (!accessToken) {
				// Generate cron expression for scheduled tasks
				let cronExpression: string | undefined;
				if (
					data.schedule === "daily" ||
					data.schedule === "weekly" ||
					data.schedule === "monthly"
				) {
					cronExpression = generateCronExpression(data) ?? undefined;
				}

				// Save task data for restoration after signup
				savePendingTask({
					instructions: data.instructions,
					schedule: data.schedule,
					cronExpression,
					permissionMode: data.permissionMode,
					selectedTools: selectedTools,
					selectedIntegrationIds: selectedIntegrationIds,
					inboundEmailEnabled: data.inboundEmailEnabled || undefined,
				});

				toast.error("Please sign up to create your first automation", {
					description: "We've saved your task and you can run it after signing up.",
					duration: 5000,
				});

				// Redirect to signup
				navigate({ to: "/signup" });
				return;
			}

			// data.integrations now contains app_integration_ids directly from the modal
			const appIntegrationIds = data.integrations ?? [];

			// Sync form state with modal selections (app_integration_ids)
			if (appIntegrationIds.length > 0) {
				setSelectedTools(appIntegrationIds.map(String));
				setSelectedIntegrationIds(appIntegrationIds);
			} else {
				setSelectedTools(["auto"]);
				setSelectedIntegrationIds([]);
			}

			// Prepare schedule-specific fields
			let scheduleFields = {};
			if (data.schedule === "interval") {
				const intervalSeconds =
					data.intervalUnit === "hours"
						? (data.intervalMinutes ?? 30) * 3600
						: (data.intervalMinutes ?? 30) * 60;
				scheduleFields = { interval_seconds: intervalSeconds };
			} else if (
				data.schedule === "daily" ||
				data.schedule === "weekly" ||
				data.schedule === "monthly"
			) {
				const cronExpression = generateCronExpression(data);
				if (cronExpression) {
					scheduleFields = {
						cron_expression: cronExpression,
						timezone: data.timezone ?? "America/Los_Angeles",
					};
				}
			}

			const requestBody: QuickCreateRequest = {
				instructions: data.instructions,
				expected_output: data.expectedOutput || null,
				goals: data.goals || null,
				integration_ids: appIntegrationIds,
				email_notifications: data.emailNotifications,
				inbound_email_enabled: data.inboundEmailEnabled,
				permission_mode: data.permissionMode,
				schedule: data.schedule,
				timezone: data.timezone ?? "America/Los_Angeles",
				...scheduleFields,
			};

			if (data.schedule === "now") {
				setIsQuickCreateStarting(true);
				start({
					instructions: data.instructions,
					requestBody: requestBody,
					accessToken,
					files: selectedFiles,
				})
					.catch((error) => {
						const statusCode = (error as Error & { statusCode?: number }).statusCode;
						const errorData = (error as Error & { data?: Record<string, unknown> }).data;

						// 402 billing limit: handleStreamError returns (no throw), so .catch() is NOT reached.
						// Upgrade modal opened by handleStreamError; confirmation modal closed by paymentErrorShownRef effect.
						if (statusCode === 402) {
							openUpgradeModalFromError(errorData);
							return;
						}

						// Close confirmation modal for thrown errors (403, generic).
						// Keeps modal open with spinner for success path (useEffect navigates on runId).
						setIsModalOpen(false);

						// Email verification required — save form so it survives page reload (mobile same-tab verify)
						if (statusCode === 403 && errorData?.error_code === "EMAIL_VERIFICATION_REQUIRED") {
							savePendingTask({
								instructions: data.instructions,
								schedule: data.schedule,
								permissionMode: data.permissionMode,
								selectedTools,
								selectedIntegrationIds,
								inboundEmailEnabled: data.inboundEmailEnabled || undefined,
							});
							return;
						}

						// Generic error for other failures
						toast.error("Failed to start task", {
							description: error.message,
						});
					})
					.finally(() => {
						setIsQuickCreateStarting(false);
					});
				return;
			}

			createTaskMutation.mutate({ requestBody });
		} catch (error) {
			const message =
				error instanceof Error ? error.message : "Unable to create task. Please try again.";
			toast.error("Failed to create task", {
				description: message,
			});
		}
	};

	const handleRunNow = async () => {
		if (!successData || !accessToken) return;
		const taskId = successData.task.id;
		setSuccessData(null);

		const runId = await executeTaskNow(taskId, accessToken);
		if (runId) {
			navigate({ to: "/runs/$runId", params: { runId } });
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		// Submit on Cmd/Ctrl+Enter
		if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
			e.preventDefault();
			if (instructions.trim()) {
				handleSubmit(e as unknown as React.FormEvent);
			}
		}
		// Cycle permission modes on Shift+Tab
		if (e.shiftKey && e.key === "Tab") {
			e.preventDefault();
			setPermissionMode((prev) => {
				const modes: Array<"safe" | "plan" | "yolo"> = ["safe", "plan", "yolo"];
				const idx = modes.indexOf(prev);
				return modes[(idx + 1) % modes.length];
			});
		}
	};

	const handleTemplateClick = (template: { prompt: string }) => {
		setInstructions(template.prompt);
		// Focus textarea and scroll to it
		setTimeout(() => {
			textareaRef.current?.focus();
			textareaRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 0);
	};

	const handleInstructionsChange = (value: string) => {
		setInstructions(value);
	};

	// Navigate to run page immediately
	useEffect(() => {
		if (runId && isStreaming) {
			setIsModalOpen(false);
			navigate({ to: "/runs/$runId", params: { runId: String(runId) } });
		}
	}, [runId, isStreaming, navigate]);

	// Handle payment errors from quick-create (402 errors dispatch PAYMENT_ERROR bubble)
	// The upgrade modal is opened by handleStreamError; here we just close the form modal.
	const paymentErrorShownRef = useRef(false);
	useEffect(() => {
		if (isQuickCreateStarting) {
			paymentErrorShownRef.current = false;
			return;
		}
		if (!isModalOpen || paymentErrorShownRef.current) {
			return;
		}
		const paymentError = bubbles.find((b) => b.type === "payment_error");
		if (paymentError) {
			setIsModalOpen(false);
			paymentErrorShownRef.current = true;
		}
	}, [bubbles, isQuickCreateStarting, isModalOpen]);

	// Restore pending task after successful signup
	useEffect(() => {
		// Only restore if authenticated
		if (!isAuthenticated) {
			return;
		}

		const pendingTask = getPendingTask();
		if (!pendingTask) {
			return;
		}

		// Restore form state — user can click submit themselves
		setInstructions(pendingTask.instructions);
		setSelectedTools(pendingTask.selectedTools);
		setSelectedIntegrationIds(pendingTask.selectedIntegrationIds);
		if (pendingTask.permissionMode) setPermissionMode(pendingTask.permissionMode);

		// Clear pending task from storage
		clearPendingTask();
	}, [isAuthenticated, setSelectedIntegrationIds, setSelectedTools]);

	const chatIsStreaming = isStreaming || isQuickCreateStarting;

	const initialMaxWidthClass = maxWidthClasses[initialMaxWidth] ?? "max-w-3xl";
	const taskInputForm = (
		<TaskInputForm
			instructions={instructions}
			placeholder={chatIsStreaming ? "Agent is working..." : placeholder}
			isStreaming={chatIsStreaming}
			placeholderVisible={placeholderVisible}
			selectedTools={selectedTools}
			combinedTools={combinedTools}
			textareaRef={textareaRef}
			onSubmit={handleSubmit}
			onInstructionsChange={handleInstructionsChange}
			onKeyDown={handleKeyDown}
			onFocus={() => setFocused(true)}
			onBlur={() => setFocused(false)}
			onToggleTool={handleToggleTool}
			onStop={stop}
			autoFocus
			variant="default"
			selectedFiles={selectedFiles}
			onFilesChange={setSelectedFiles}
			permissionMode={permissionMode}
			onPermissionModeChange={setPermissionMode}
		/>
	);

	// Task input with templates
	return (
		<div
			className={cn(
				"mx-auto w-full",
				initialMaxWidthClass,
				isAuthenticated ? "px-2 py-8 lg:px-4 lg:py-10" : "px-3 py-8 sm:px-4 sm:py-10 lg:py-14"
			)}
		>
			<div className={cn("space-y-8", !isAuthenticated && "space-y-9 lg:space-y-10")}>
				<section
					data-testid={!isAuthenticated ? "landing-hero-section" : undefined}
					className={cn("space-y-4", !isAuthenticated && "space-y-5 lg:space-y-6")}
				>
					<TaskCreationHero isAuthenticated={isAuthenticated} heading={heading} />
					{taskInputForm}
				</section>

				{/* Task Confirmation Modal */}
				<TaskConfirmationModal
					open={isModalOpen}
					onOpenChange={setIsModalOpen}
					instructions={instructions}
					onSubmit={handleModalSubmit}
					isSubmitting={createTaskMutation.isPending || isQuickCreateStarting}
					isEditMode={false}
					defaultIntegrations={selectedIntegrationIds}
					showInstructionsField={false}
					defaultPermissionMode={permissionMode}
				/>

				<ScheduledTaskSuccessDialog
					successData={successData}
					onOpenChange={(open) => {
						if (!open) setSuccessData(null);
					}}
					onSeeTeammate={() => {
						const agentId = successData?.agent.id;
						setSuccessData(null);
						navigate({
							to: "/teammates/$id",
							params: { id: String(agentId) },
						});
					}}
					onRunNow={handleRunNow}
					onCreateAnother={() => setSuccessData(null)}
				/>

				<WebhookUrlDialog
					webhookUrl={webhookDialogUrl}
					onOpenChange={(open) => {
						if (!open) setWebhookDialogUrl(null);
					}}
				/>

				{!isAuthenticated ? (
					<section className="space-y-6 pt-1 sm:space-y-7">
						<TemplateGallery onTemplateClick={handleTemplateClick} />
						<UnauthenticatedHowItWorks />
					</section>
				) : (
					<div className="space-y-6 pt-1">
						<TemplateGallery onTemplateClick={handleTemplateClick} />
					</div>
				)}
			</div>
		</div>
	);
}
type QuickCreateRequest = components["schemas"]["QuickCreateTaskRequest"];
type QuickCreateResponse = components["schemas"]["QuickCreateTaskResponse"];
